import React, { useState } from 'react';
import { useT } from '../i18n';

interface OnboardingProps {
    onComplete: () => void;
}

// CF-REMOVE-MARGIN-CLAIMS-V1 (PL #7258): house-cut framing removed from onboarding.
// "Provably Fair" slide retained — verification primitive is HMAC-signed flip
// outcomes that any player can check via the Verify feature.
// Localized copy resolves from i18n keys inside the component (useT); the const
// carries only the non-text presentation fields + the key prefix per slide.
const SLIDES = [
    {
        emoji: '⚡',
        key: 'slide1',
        color: 'var(--gold)',
        bg: 'rgba(244, 213, 111, 0.06)',
    },
    {
        emoji: '💎',
        key: 'slide2',
        color: 'var(--emerald)',
        bg: 'rgba(16, 185, 129, 0.06)',
    },
    {
        emoji: '🔐',
        key: 'slide3',
        color: '#3b82f6',
        bg: 'rgba(59, 130, 246, 0.06)',
    },
];

export function Onboarding({ onComplete }: OnboardingProps) {
    const t = useT();
    const [slide, setSlide] = useState(0);

    // CF-FUNNEL-INSTANT-PLAY-V1: the intro no longer persists a `cf_onboarded` flag — it is
    // no longer an auto-gate (App decides auto-show via shouldShowOnboarding = fresh-only, and
    // re-shows it on demand from the ⋯ "How it works" menu). This component just renders the
    // slides and signals completion; no stored state is written.
    const next = () => {
        if (slide < SLIDES.length - 1) {
            setSlide(slide + 1);
        } else {
            onComplete();
        }
    };

    const skip = () => {
        onComplete();
    };

    const s = SLIDES[slide];

    return (
        <div className="min-h-screen min-h-[100dvh] flex flex-col items-center justify-center px-6"
             style={{ background: 'var(--bg-deep)' }}>

            {/* Skip */}
            <button onClick={skip}
                    className="absolute top-4 right-4 text-[11px] font-semibold px-3 py-1.5 rounded-full"
                    style={{ color: 'var(--text-muted)', background: 'var(--bg-card)' }}>
                {t('onboarding.skip')}
            </button>

            {/* Slide content */}
            <div className="text-center max-w-xs">
                {/* Icon */}
                <div className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-8"
                     style={{ background: s.bg, border: `1px solid ${s.color}22` }}>
                    <span className="text-5xl">{s.emoji}</span>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-black tracking-tight mb-3" style={{ color: 'var(--text-primary)' }}>
                    {t(`onboarding.${s.key}.title`)}
                </h1>

                {/* Description */}
                <p className="text-[13px] leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>
                    {t(`onboarding.${s.key}.desc`)}
                </p>

                {/* Highlight badge */}
                <div className="inline-block rounded-full px-4 py-1.5"
                     style={{ background: s.bg, border: `1px solid ${s.color}33` }}>
                    <span className="text-[11px] font-bold" style={{ color: s.color }}>
                        {t(`onboarding.${s.key}.highlight`)}
                    </span>
                </div>
            </div>

            {/* Navigation */}
            <div className="mt-12 w-full max-w-xs">
                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 mb-6">
                    {SLIDES.map((_, i) => (
                        <div key={i} className="rounded-full transition-all duration-300"
                             style={{
                                 width: i === slide ? 24 : 6,
                                 height: 6,
                                 background: i === slide ? s.color : 'var(--bg-elevated)',
                             }} />
                    ))}
                </div>

                {/* Button */}
                <button onClick={next}
                        className="btn-primary w-full py-4 rounded-2xl text-sm font-bold tracking-wide">
                    {slide < SLIDES.length - 1 ? t('onboarding.next') : t('onboarding.start')}
                </button>
            </div>
        </div>
    );
}
